<script setup>
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Card } from '@drama/ui'

import { api } from '../api.js'

const { t } = useI18n()
const data = ref(null)
const error = ref('')

// 分母为 0 的指标后端给 null，显示为「—」。
const plain = (value) => (value === null ? '—' : value)
const percent = (value) => (value === null ? '—' : `${Math.round(value * 100)}%`)

onMounted(async () => {
  try {
    data.value = (await api('GET', '/api/voko/admin/dashboard')).dashboard
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
})
</script>

<template>
  <h1>{{ t('adminDashboard.title') }}</h1>
  <p v-if="error" role="alert">{{ error }}</p>
  <template v-if="data">
    <Card class="voko-card">
      <h2>{{ t('adminDashboard.weekly') }}</h2>
      <div class="voko-table-scroll">
        <table class="voko-weeks">
          <thead>
            <tr>
              <th scope="col">{{ t('adminDashboard.week') }}</th>
              <th scope="col">{{ t('adminDashboard.settled') }}</th>
              <th scope="col">{{ t('adminDashboard.earned') }}</th>
              <th scope="col">{{ t('adminDashboard.cycle') }}</th>
              <th scope="col">{{ t('adminDashboard.firstPass') }}</th>
              <th scope="col">{{ t('adminDashboard.adoption') }}</th>
              <th scope="col">{{ t('adminDashboard.independent') }}</th>
              <th scope="col">{{ t('adminDashboard.helpPerTask') }}</th>
              <th scope="col">{{ t('adminDashboard.aiAgreement') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="week in data.weeks" :key="week.week_start">
              <th scope="row">{{ week.week_start }}</th>
              <td>{{ week.tasks_settled }}</td>
              <td>{{ week.points_earned }}</td>
              <td>{{ plain(week.avg_delivery_hours) }}</td>
              <td>{{ percent(week.first_pass_rate) }}</td>
              <td>{{ percent(week.adoption_rate) }}</td>
              <td>{{ percent(week.independent_rate) }}</td>
              <td>{{ plain(week.avg_help_requests) }}</td>
              <td>{{ percent(week.ai_agreement_rate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>{{ t('adminDashboard.disputes') }}：{{ data.open_disputes }}</p>
    </Card>
    <Card class="voko-card">
      <h2>{{ t('adminDashboard.effortRatios') }}</h2>
      <div class="voko-table-scroll">
        <table class="voko-weeks">
          <thead>
            <tr>
              <th scope="col">{{ t('adminDashboard.kind') }}</th>
              <th scope="col">{{ t('adminDashboard.samples') }}</th>
              <th scope="col">{{ t('adminDashboard.medianRatio') }}</th>
              <th scope="col">{{ t('adminDashboard.calibration') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ratio in data.effort_ratios" :key="ratio.kind">
              <th scope="row">{{ t(`board.kinds.${ratio.kind}`) }}</th>
              <td>{{ ratio.samples }}</td>
              <td>{{ plain(ratio.median_ratio) }}</td>
              <td>{{ t(`adminDashboard.${ratio.calibrated ? 'calibrated' : 'uncalibrated'}`) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
    <Card class="voko-card">
      <h2>{{ t('adminDashboard.tasks') }}</h2>
      <p v-for="(count, status) in data.tasks_by_status" :key="status">{{ t(`board.status.${status}`) }}：{{ count }}</p>
      <h2>{{ t('adminDashboard.agents') }}</h2>
      <p v-for="(count, status) in data.agents_by_status" :key="status">{{ t(`agents.status.${status}`) }}：{{ count }}</p>
    </Card>
  </template>
</template>

<style scoped>
.voko-card {
  margin-bottom: var(--drama-space-md);
}
.voko-table-scroll {
  overflow-x: auto;
}
.voko-weeks {
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
}
.voko-weeks th,
.voko-weeks td {
  padding: var(--drama-space-sm);
  text-align: right;
  white-space: nowrap;
}
.voko-weeks thead th {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
  font-weight: 400;
  white-space: normal;
}
.voko-weeks th:first-child {
  text-align: left;
}
</style>
